import { useState } from 'react';
import {
  useCurrentUser,
  CURRENT_USER_QUERY
} from '../../lib/core/useCurrentUser';
import { useQuery, useMutation } from '@apollo/client';
import {
  payoffUnpaidOrdersMutation,
  currentUserUnpaidOrdersQuery,
  currentUserQuery
} from '../../core-types';
import { FormattedMessage, useIntl, defineMessage } from 'react-intl';
import { WarningAlt } from '@urbaninfrastructure/react-icons';
import {
  Alert,
  Box,
  Flex,
  Text,
  Link,
  LoadableButton,
  AnchorButton
} from '../../components';
import { FormattedCurrency } from '../../components/FormattedCurrency';
import { globalMessages } from '../../lib/messages';
import { ChargePaymentCardMessage } from '../../components/ChargePaymentCardMessage';
import { useUnpaidOrders } from './useUnpaidOrders';
import {
  PAY_OFF_UNPAID_ORDERS_MUTATION,
  CURRENT_USER_UNPAID_ORDERS_QUERY
} from './graphql';
import { useToasts } from '../../components/Toasts';

const POLL_RETRIES = 10;

const succesfullyPaidInvoiceMessage = defineMessage({
  id: 'unpaid_orders.paid_off_successfully',
  defaultMessage: 'Unpaid invoices have been paid'
});

export const PayUnpaidOrders = ({
  pollInterval = 1000
}: {
  pollInterval?: number;
}) => {
  const intl = useIntl();
  const { addToast } = useToasts();
  const { currentUser } = useCurrentUser();
  const { unpaidOrders } = useUnpaidOrders();
  const [hasUnpaidOrdersStatus, setHasUnpaidOrdersStatus] = useState<{
    loading: boolean;
    timedOut: boolean;
    retries: number;
  }>({ loading: false, timedOut: false, retries: 0 });

  const [payoffUnpaidOrders, { error, loading, data }] = useMutation<
    payoffUnpaidOrdersMutation
  >(PAY_OFF_UNPAID_ORDERS_MUTATION);

  const currentUserHasUnpaidOrdersResponse = useQuery<
    currentUserUnpaidOrdersQuery
  >(CURRENT_USER_UNPAID_ORDERS_QUERY, {
    skip: !data,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: () => {
      setHasUnpaidOrdersStatus({
        loading: false,
        timedOut: false,
        retries: hasUnpaidOrdersStatus.retries
      });
    },
    onCompleted: data => {
      if (!data) {
        return;
      }
      if (hasUnpaidOrdersStatus.retries >= POLL_RETRIES) {
        currentUserHasUnpaidOrdersResponse.stopPolling();
        setHasUnpaidOrdersStatus({
          loading: false,
          timedOut: true,
          retries: hasUnpaidOrdersStatus.retries
        });
      } else if (
        data.currentUser &&
        data.currentUser.unpaidOrders &&
        data.currentUser.unpaidOrders.length === 0
      ) {
        currentUserHasUnpaidOrdersResponse.stopPolling();
        setHasUnpaidOrdersStatus({
          loading: false,
          timedOut: false,
          retries: hasUnpaidOrdersStatus.retries
        });
        addToast({
          text: intl.formatMessage(succesfullyPaidInvoiceMessage),
          type: 'success'
        });
        const { client } = currentUserHasUnpaidOrdersResponse;
        const cachedData = client.readQuery<currentUserQuery>({
          query: CURRENT_USER_QUERY
        });
        if (cachedData && cachedData.currentUser) {
          client.writeQuery<currentUserQuery>({
            query: CURRENT_USER_QUERY,
            data: {
              ...cachedData,
              currentUser: { ...cachedData.currentUser, hasUnpaidOrders: false }
            }
          });
        }
      } else {
        currentUserHasUnpaidOrdersResponse.startPolling(pollInterval);
        setHasUnpaidOrdersStatus({
          loading: true,
          timedOut: false,
          retries: hasUnpaidOrdersStatus.retries + 1
        });
      }
    }
  });

  if (unpaidOrders.length === 0) {
    return null;
  }

  const unpaidOrdersAmount = unpaidOrders.reduce<number>(
    (accum, unpaidOrder) => {
      if (unpaidOrder.amount !== null) {
        accum += unpaidOrder.amount;
      }
      return accum;
    },
    0
  );

  const responseState =
    error ||
    currentUserHasUnpaidOrdersResponse.error ||
    hasUnpaidOrdersStatus.timedOut
      ? 'error'
      : 'success';

  return (
    <Flex alignItems="center" flexDirection="column" mb="sm">
      <Box mb="xs">
        <WarningAlt size={32} color="state.error" />
      </Box>
      <Text as="p" data-testid="summary">
        <FormattedMessage
          id="PayUnpaidOrders.summary"
          defaultMessage="You have {pluralUnpaidInvoices} for a total of {unpaidOrdersAmount}"
          values={{
            pluralUnpaidInvoices: (
              <FormattedMessage
                id="PayUnpaidOrders.pluralUnpaidInvoices"
                defaultMessage="{numberOfOrders, plural, one {an unpaid invoice} other {{numberOfOrders} unpaid invoices}}"
                values={{ numberOfOrders: unpaidOrders.length }}
              />
            ),
            unpaidOrdersAmount: <FormattedCurrency value={unpaidOrdersAmount} />
          }}
        />
      </Text>
      <LoadableButton
        data-testid="repay"
        variant="primary"
        shape="cool"
        loading={
          loading ||
          currentUserHasUnpaidOrdersResponse.loading ||
          hasUnpaidOrdersStatus.loading
        }
        responseState={responseState}
        onClick={() => {
          setHasUnpaidOrdersStatus({
            loading: true,
            timedOut: false,
            retries: 0
          });
          payoffUnpaidOrders();
        }}
      >
        <FormattedMessage id="receipts_repay" defaultMessage="Repay" />{' '}
        <FormattedCurrency value={unpaidOrdersAmount} />
      </LoadableButton>
      {currentUser?.paymentMethod &&
        currentUser.paymentMethod.__typename === 'Card' && (
          <Text mute mt="xxs" textAlign="center">
            <ChargePaymentCardMessage
              last4={currentUser.paymentMethod.last4}
              displayBrand={currentUser.paymentMethod.displayBrand}
            />

            <Link route="profile.credit-cards" passHref>
              <AnchorButton>
                <FormattedMessage
                  id="PayUnpaidOrders.button_manage_card"
                  defaultMessage="Change payment card"
                />
              </AnchorButton>
            </Link>
          </Text>
        )}
      {responseState === 'error' && (
        <Alert variant="error" mt="xs">
          <FormattedMessage {...globalMessages.genericError} />
        </Alert>
      )}
    </Flex>
  );
};
